import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import Button from "../components/common/Button";
import TextInput from "../components/common/TextInput";
import Meta from "../components/Meta";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";

/* =============================== Category Groups =============================== */

const CATEGORY_GROUPS = [
  {
    name: "Home Repair",
    color: "#d97706",
    categories: ["Drywall", "Electrical Work", "Plumbing", "Handyman Services",
      "Painting", "Flooring", "Fencing", "Roof Services", "HVAC Services", "Carpentry"]
  },
  {
    name: "Outdoor",
    color: "#16a34a",
    categories: ["Landscaping", "Yard Work", "Gardening", "Pool Services", "Pressure Washing"]
  },
  {
    name: "Moving & Hauling",
    color: "#7c3aed",
    categories: ["Moving Help", "Junk Removal", "Hauling", "Delivery & Pickup"]
  },
  {
    name: "Cleaning & Organization",
    color: "#0891b2",
    categories: ["House Cleaning", "Home Organization"]
  },
  {
    name: "Assembly & Installation",
    color: "#db2777",
    categories: ["Furniture Assembly", "General Mounting", "Appliance Installation"]
  },
  {
    name: "Professional Services",
    color: "#2563eb",
    categories: ["Computer & Tech Help", "Tutoring & Lessons", "Photography",
      "Writing & Editing", "Design & Creative", "Administrative Support"]
  },
  {
    name: "Personal Services",
    color: "#ea580c",
    categories: ["Pet Care", "Caregiving Services", "Cooking / Meal Prep",
      "Sewing or Clothing Repairs", "Rides & Transport"]
  },
  {
    name: "Events",
    color: "#9333ea",
    categories: ["Event Services"]
  },
  {
    name: "Other",
    color: "#64748b",
    categories: ["Other"]
  }
];

/* =============================== Styling =============================== */

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    error: "#dc2626",
    errorLight: "#fef2f2",
  },
  shadows: {
    sm: "0 1px 3px rgba(15, 23, 42, 0.06)",
    md: "0 4px 16px rgba(15, 23, 42, 0.08)",
    lg: "0 8px 32px rgba(15, 23, 42, 0.12)",
  },
  borderRadius: "16px",
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fafbff 0%, #f8fafc 50%, #f1f5f9 100%);
`;

const OnboardingContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #f1f5f9;
  border-radius: ${theme.borderRadius};
  margin: 40px auto;
  width: 100%;
  max-width: 700px;
  padding: 40px;
  box-shadow: ${theme.shadows.lg};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    margin: 24px auto;
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding: 32px 24px;
  background: linear-gradient(135deg, ${theme.colors.primary}08 0%, ${theme.colors.primaryLight} 40%, #fafbff 100%);
  border-radius: 20px;
  border: 2px solid ${theme.colors.primary}20;
  
  h1 {
    margin: 0 0 12px 0;
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: none;

    @media (max-width: 768px) {
      font-size: 30px;
    }

    @media (max-width: 480px) {
      font-size: 26px;
      letter-spacing: -0.01em;
    }
  }
  
  p {
    margin: 0;
    font-size: 17px;
    color: ${theme.colors.textBody};
    line-height: 1.6;
    font-weight: 600;
    max-width: 500px;
    margin: 0 auto;

    @media (max-width: 768px) {
      font-size: 16px;
    }

    @media (max-width: 480px) {
      font-size: 15px;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 32px;
  background: ${theme.colors.surface};
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: ${theme.shadows.sm};
  position: relative;
  
  h2 {
    margin: 0 0 16px 0;
    font-size: 20px;
    font-weight: 700;
    color: ${theme.colors.textHeading};
    letter-spacing: -0.02em;
  }
  
  p {
    margin: 0 0 20px 0;
    font-size: 14px;
    color: ${theme.colors.textMuted};
    line-height: 1.5;
    font-weight: 500;
  }
`;

const ProfessionalQuestion = styled.div`
  background: linear-gradient(135deg, ${theme.colors.surface} 0%, #fafbff 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #f1f5f9;
  position: relative;
  box-shadow: ${theme.shadows.sm};
  
  h3 {
    margin: 0 0 16px 0;
    font-size: 18px;
    font-weight: 700;
    color: ${theme.colors.textHeading};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
  
  label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    font-size: 15px;
    color: ${theme.colors.textBody};
    font-weight: 600;
    padding: 12px 16px;
    background: ${theme.colors.surface};
    border-radius: 12px;
    border: 2px solid ${theme.colors.border};
    transition: all 0.2s ease;
    flex: 1;
    min-width: 200px;
    
    &:hover {
      border-color: ${theme.colors.primary};
      background: #fafbff;
      transform: translateY(-1px);
    }
    
    input[type="radio"] {
      margin: 0;
      accent-color: ${theme.colors.primary};
      transform: scale(1.2);
    }
    
    input[type="radio"]:checked + span {
      color: ${theme.colors.primary};
    }
  }
`;

const CategoryGroupsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const CategoryGroup = styled.div`
  background: ${theme.colors.surface};
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid ${theme.colors.border};
  border-left: 2px solid ${props => props.accentColor || theme.colors.border};
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .group-name {
    font-size: 13px;
    font-weight: 600;
    color: ${theme.colors.textHeading};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .group-count {
    font-size: 11px;
    color: ${theme.colors.primary};
    font-weight: 600;
    background: ${theme.colors.primaryLight};
    padding: 2px 8px;
    border-radius: 10px;
    margin-left: auto;
  }
`;

const CategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CategoryChip = styled.div`
  background: ${props => props.selected ? theme.colors.primary : theme.colors.surface};
  border: 1px solid ${props => props.selected ? theme.colors.primary : theme.colors.border};
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${props => props.selected ? '#2563eb' : theme.colors.primaryLight};
  }

  .name {
    font-weight: 500;
    color: ${props => props.selected ? '#ffffff' : theme.colors.textBody};
    font-size: 13px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  font-size: 14px;
  cursor: pointer;
  padding: 12px 0;
  font-weight: 500;
  
  &:hover {
    color: ${theme.colors.textBody};
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.errorLight};
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  color: ${theme.colors.error};
  font-size: 14px;
  font-weight: 500;
`;

/* =============================== Component =============================== */

const Onboarding = ({ state, dispatch }) => {
  const navigate = useNavigate();
  const [isProfessional, setIsProfessional] = useState(null);
  const [zipcode, setZipcode] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [currentKeyword, setCurrentKeyword] = useState("");
  const [keywordError, setKeywordError] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const response = await request("POST", API_CONSTANT_MAP.fetch_categories);
      if (response.data?.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
      setError("Failed to load categories. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const addKeyword = () => {
    const keyword = currentKeyword.trim();
    if (!keyword) return;
    
    const words = keyword.split(/\s+/).filter(word => word.length > 0);
    if (words.length > 2) {
      setKeywordError("Keep keywords 1 or 2 words maximum. Examples: painting, drywall, ring doorbell");
      return;
    }
    
    if (keywords.includes(keyword.toLowerCase())) {
      setKeywordError("This keyword is already added");
      return;
    }
    
    setKeywords(prev => [...prev, keyword.toLowerCase()]);
    setCurrentKeyword("");
    setKeywordError("");
  };

  const removeKeyword = (keywordToRemove) => {
    setKeywords(prev => prev.filter(keyword => keyword !== keywordToRemove));
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      // Call backend to record skip
      const response = await request("POST", API_CONSTANT_MAP.process_onboarding, { action: "skip" });
      
      if (response.data?.success) {
        // Update Redux store with new onboarding status
        dispatch({ type: "UPDATE_ONBOARDING_STATUS", payload: "skipped" });
      }
      
      // Navigate regardless of response
      const urlParams = new URLSearchParams(window.location.search);
      const redirectTo = urlParams.get('redirect');
      navigate(redirectTo || "/");
    } catch (error) {
      console.error("Failed to record skip:", error);
      // Still navigate even if recording skip fails
      const urlParams = new URLSearchParams(window.location.search);
      const redirectTo = urlParams.get('redirect');
      navigate(redirectTo || "/");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isProfessional && !zipcode.trim()) {
      setError("Please enter your zipcode to receive notifications for tasks in your area.");
      return;
    }

    if (isProfessional && selectedCategories.length === 0) {
      setError("Please select at least one category you're interested in.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Call backend to process onboarding
      const onboardingData = {
        is_professional: isProfessional,
        zipcode: isProfessional ? zipcode.trim() : null,
        selected_categories: isProfessional ? selectedCategories : [],
        keywords: isProfessional ? keywords : []
      };

      const response = await request("POST", API_CONSTANT_MAP.process_onboarding, onboardingData);
      
      if (response.data?.success) {
        // Update Redux store with new onboarding status
        dispatch({ type: "UPDATE_ONBOARDING_STATUS", payload: "completed" });
        
        // Success - navigate to main page or redirect URL
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirect');
        navigate(redirectTo || "/");
      } else {
        setError(response.data?.error?.message || "Failed to save your preferences. Please try again.");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      setError(error.error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = isProfessional === false || (isProfessional === true && zipcode.trim() && selectedCategories.length > 0);

  return (
    <Meta pageMeta={{ title: "Welcome to Doitly - Set up your preferences" }}>
      <NavBar user={state.user} />
      <PageWrapper>
        <OuterContainer>
          <OnboardingContainer>
            <Header>
              <h1>Welcome to Doitly</h1>
              <p>Set up your notification preferences to find the perfect opportunities</p>
            </Header>

            <ProfessionalQuestion>
              <h3>Are you looking to earn money by completing tasks for others? <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span></h3>
              <RadioGroup>
                <label>
                  <input
                    type="radio"
                    name="professional"
                    value="yes"
                    checked={isProfessional === true}
                    onChange={() => setIsProfessional(true)}
                  />
                  <span>Yes, I want to find work opportunities</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="professional"
                    value="no"
                    checked={isProfessional === false}
                    onChange={() => setIsProfessional(false)}
                  />
                  <span>No, I'm here to post tasks for others</span>
                </label>
              </RadioGroup>
            </ProfessionalQuestion>

            {isProfessional === true && (
              <>
                <Section>
                  <h2>Your Location <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span></h2>
                  <p>Enter your zipcode to get notifications for tasks in your area</p>
                  <TextInput
                    value={zipcode}
                    name="zipcode"
                    label="Zipcode"
                    onChange={(e) => setZipcode(e.target.value)}
                    placeholder="e.g., 94568"
                  />
                </Section>

                <Section>
                  <h2>Categories You're Interested In <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span></h2>
                  <p>Select the types of tasks you'd like to be notified about</p>
                  
                  {isLoading ? (
                    <p>Loading categories...</p>
                  ) : (
                    <CategoryGroupsContainer>
                      {CATEGORY_GROUPS.map((group) => {
                        // Filter categories that exist in this group
                        const groupCategories = categories.filter(cat =>
                          group.categories.includes(cat.name)
                        );

                        // Skip empty groups
                        if (groupCategories.length === 0) return null;

                        // Count selected in this group
                        const selectedInGroup = groupCategories.filter(cat =>
                          selectedCategories.includes(cat.id)
                        ).length;

                        return (
                          <CategoryGroup key={group.name} accentColor={group.color}>
                            <GroupHeader>
                              <span className="group-name">{group.name}</span>
                            </GroupHeader>
                            <CategoryChips>
                              {groupCategories.map(category => (
                                <CategoryChip
                                  key={category.id}
                                  selected={selectedCategories.includes(category.id)}
                                  onClick={() => handleCategorySelect(category.id)}
                                >
                                  <span className="name">{category.name}</span>
                                </CategoryChip>
                              ))}
                            </CategoryChips>
                          </CategoryGroup>
                        );
                      })}
                    </CategoryGroupsContainer>
                  )}
                </Section>

                <div style={{
                    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    margin: '32px 0 24px 0',
                    border: '2px solid #16a34a20',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#166534',
                      letterSpacing: '-0.01em'
                    }}>
                      ✨ You can also get notified for keywords
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#15803d',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      Get additional notifications when tasks mention specific terms
                    </div>
                  </div>

                <Section>
                  <h2>Keywords (Optional)</h2>
                  <p>In addition to your selected categories, you'll get notifications when task titles or descriptions contain these keywords.</p>
                  <div style={{ 
                    background: `${theme.colors.primaryLight}`, 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '16px',
                    border: `1px solid ${theme.colors.primary}30`
                  }}>
                    <strong style={{ color: theme.colors.primary, fontSize: '0.9rem' }}>Examples:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '0.85rem', color: theme.colors.textBody }}>
                      <li><strong>For Handyman:</strong> plumbing, electrical, drywall, painting, tile installation</li>
                      <li><strong>For Tutoring:</strong> calculus, SAT prep, Spanish, piano, coding</li>
                      <li><strong>For Design:</strong> logo design, Photoshop, web design, branding</li>
                      <li><strong>For Writing:</strong> copywriting, technical writing, blog posts, editing</li>
                    </ul>
                  </div>
                  {/* Existing keywords */}
                  {keywords.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: theme.colors.textMuted,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Your Keywords ({keywords.length})
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '8px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #fafbff 0%, #f8fafc 100%)',
                        borderRadius: '12px',
                        border: '2px solid #f1f5f9',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                      }}>
                        {keywords.map((keyword, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              color: '#b45309',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '600',
                              border: '1px solid #f59e0b20',
                              boxShadow: '0 2px 4px rgba(180, 83, 9, 0.1)',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {keyword}
                            <button
                              type="button"
                              onClick={() => removeKeyword(keyword)}
                              style={{
                                marginLeft: '8px',
                                background: 'none',
                                border: 'none',
                                color: '#b45309',
                                cursor: 'pointer',
                                fontSize: '16px',
                                lineHeight: '1',
                                padding: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '50%',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#b4530920';
                                e.target.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'none';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add keyword input */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.colors.textMuted,
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Add Keywords
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px',
                      background: 'linear-gradient(135deg, #fafbff 0%, #f8fafc 100%)',
                      borderRadius: '12px',
                      border: '2px solid #f1f5f9'
                    }}>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          value={currentKeyword}
                          name="currentKeyword"
                          label=""
                          onChange={(e) => {
                            setCurrentKeyword(e.target.value);
                            setKeywordError("");
                          }}
                          onKeyPress={handleKeywordKeyPress}
                          placeholder="e.g., painting, drywall, ring doorbell"
                          style={{ 
                            background: 'transparent',
                            border: 'none',
                            margin: '0',
                            width: '100%'
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={addKeyword}
                        disabled={!currentKeyword.trim()}
                        style={{
                          padding: '10px 20px',
                          background: currentKeyword.trim() 
                            ? `linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%)` 
                            : '#94a3b8',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: currentKeyword.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: currentKeyword.trim() 
                            ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                            : 'none',
                          transform: currentKeyword.trim() ? 'translateY(0)' : 'none',
                          flexShrink: 0,
                          height: 'fit-content'
                        }}
                        onMouseEnter={(e) => {
                          if (currentKeyword.trim()) {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentKeyword.trim()) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                          }
                        }}
                      >
                        Add
                      </Button>
                    </div>
                    {keywordError && (
                      <div style={{ 
                        color: '#dc2626', 
                        fontSize: '13px', 
                        marginTop: '8px',
                        fontWeight: '600',
                        background: '#fef2f2',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        ⚠️ {keywordError}
                      </div>
                    )}
                  </div>
                  
                  <div style={{
                    color: theme.colors.textMuted,
                    fontSize: '13px',
                    marginTop: '8px',
                    fontWeight: '500',
                    background: '#f8fafc',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    💡 <span>Keep keywords 1-2 words maximum for better matching</span>
                  </div>
                </Section>
              </>
            )}

            {isProfessional === false && (
              <Section style={{ 
                background: `linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)`, 
                border: `2px solid ${theme.colors.success}30`,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
                  animation: 'pulse 3s ease-in-out infinite'
                }}></div>
                <h2 style={{ 
                  color: theme.colors.success, 
                  fontSize: '24px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  🎉 Perfect!
                </h2>
                <p style={{ 
                  color: theme.colors.textBody, 
                  fontSize: '16px',
                  fontWeight: '500',
                  lineHeight: '1.6',
                  position: 'relative',
                  zIndex: 1
                }}>
                  You're all set to start posting tasks and finding great service providers on Doitly!
                </p>
                <div style={{
                  marginTop: '16px',
                  padding: '12px 20px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  fontSize: '14px',
                  color: '#047857',
                  fontWeight: '600'
                }}>
                  ✨ Ready to post your first task?
                </div>
              </Section>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ActionButtons>
              <SkipButton onClick={handleSkip}>
                Skip for now
              </SkipButton>
              
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                style={{
                  background: canSubmit 
                    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%)` 
                    : "#94a3b8",
                  borderRadius: "12px",
                  fontWeight: 700,
                  padding: "14px 40px",
                  opacity: isSubmitting ? 0.7 : 1,
                  border: "none",
                  color: theme.colors.surface,
                  fontSize: "15px",
                  cursor: canSubmit && !isSubmitting ? "pointer" : "not-allowed",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: canSubmit 
                    ? "0 8px 25px rgba(59, 130, 246, 0.35)" 
                    : "none",
                  transform: canSubmit && !isSubmitting ? "translateY(0)" : "none",
                  letterSpacing: "0.025em",
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (canSubmit && !isSubmitting) {
                    e.target.style.transform = "translateY(-2px) scale(1.02)";
                    e.target.style.boxShadow = "0 12px 35px rgba(59, 130, 246, 0.45)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (canSubmit && !isSubmitting) {
                    e.target.style.transform = "translateY(0) scale(1)";
                    e.target.style.boxShadow = "0 8px 25px rgba(59, 130, 246, 0.35)";
                  }
                }}
              >
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {isProfessional === true ? 
                    (isSubmitting ? "⏳ Setting up..." : "🚀 Set up notifications") :
                    (isSubmitting ? "⏳ Finishing..." : "✨ Continue")
                  }
                </span>
              </Button>
            </ActionButtons>
          </OnboardingContainer>
        </OuterContainer>
      </PageWrapper>
    </Meta>
  );
};

const mapStateToProps = (state) => {
  return { state };
};

export default connect(mapStateToProps)(Onboarding);